// @flow
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import {
  type ComponentType,
  useMemo,
  useRef,
  useEffect,
  useCallback
} from 'react';
import { typography as getTypography } from '@tablekit/theme';
import type { LevelType } from '@tablekit/theme/lib/types';
import { mediaQuery } from '@tablekit/utils';
import type { MediaQueryType } from '@tablekit/utils/types';
import { splitWords } from '../utils/wordDetection';

type PropsType = {
  Component: ComponentType<any>,
  text: string,
  typography?: MediaQueryType<{
    forceExpand?: boolean,
    level: string,
    lines: number
  }>
};

const ellipsisId = 'ellipsis';

export function buildTruncationCss(
  level: LevelType,
  lines: number,
  forceExpand?: boolean
) {
  return (props: *) => {
    const typographyConfig = getTypography(level)(props);
    const lineHeight = parseInt(typographyConfig.lineHeight, 10);
    const maxHeight = 1 + lineHeight * lines;
    return css`
      ${typographyConfig};
      max-height: ${maxHeight}px;
      ${forceExpand ? `height: ${maxHeight}px;` : ''}
    `;
  };
}

const WrapperSpan = styled.span`
  overflow: hidden;
  position: relative;
  display: inline-block;

  span {
    line-height: inherit;
  }

  #${ellipsisId} ~ span {
    opacity: 0;
  }

  ${mediaQuery('typography', (props) => {
    if (!props) {
      return css`
        font-size: inherit;
        line-height: inherit;
        max-height: inherit;
        height: inherit;
      `;
    }
    const { level, lines, forceExpand } = props;
    return buildTruncationCss(level, lines, forceExpand)(props);
  })}
`;

const TruncatedText = ({ Component, typography, text }: PropsType) => {
  const textWrapperRef = useRef();
  const wrapperRef = useRef();
  const testSize = useCallback(() => {
    if (!textWrapperRef.current || !wrapperRef.current) {
      return;
    }
    const shouldShow =
      textWrapperRef.current.offsetHeight > wrapperRef.current.offsetHeight &&
      textWrapperRef.current.children.length;
    const currentEllipsisElement = textWrapperRef.current.querySelector(
      `#${ellipsisId}`
    );
    if (
      !shouldShow &&
      currentEllipsisElement &&
      currentEllipsisElement.parentNode
    ) {
      currentEllipsisElement.parentNode.removeChild(currentEllipsisElement);
    } else if (shouldShow) {
      window.requestAnimationFrame(() => {
        if (!textWrapperRef.current || !wrapperRef.current) return;
        const textWrapperElement: HTMLElement = textWrapperRef.current;
        const wrapperElement: HTMLElement = wrapperRef.current;
        const currentEllipsis = textWrapperElement.querySelector(
          `#${ellipsisId}`
        );
        if (currentEllipsis && currentEllipsis.parentNode) {
          currentEllipsis.parentNode.removeChild(currentEllipsis);
        }
        const textSpans = textWrapperElement.children;
        const lastTextSpan = textSpans[textSpans.length - 1];
        if (
          lastTextSpan.offsetTop + lastTextSpan.offsetHeight <
          wrapperElement.offsetHeight
        ) {
          return;
        }
        let lastNodeIndex = 0;
        while (
          lastNodeIndex < textSpans.length - 2 &&
          textSpans[lastNodeIndex + 1].offsetTop < wrapperElement.offsetHeight
        ) {
          lastNodeIndex += 1;
        }
        const ellipsisSpan = document.createElement('span');
        ellipsisSpan.setAttribute('id', ellipsisId);
        ellipsisSpan.innerHTML = '...';
        lastNodeIndex += 1;
        textWrapperElement.insertBefore(ellipsisSpan, textSpans[lastNodeIndex]);
        while (
          ellipsisSpan.offsetTop + ellipsisSpan.offsetHeight >
            wrapperElement.offsetHeight &&
          lastNodeIndex > 0
        ) {
          if (ellipsisSpan.parentNode) {
            ellipsisSpan.parentNode.removeChild(ellipsisSpan);
          }
          lastNodeIndex -= 1;
          textWrapperElement.insertBefore(
            ellipsisSpan,
            textSpans[lastNodeIndex]
          );
        }
      });
    }
  }, []);
  useEffect(() => {
    window.addEventListener('resize', testSize);
    return () => window.removeEventListener('resize', testSize);
  }, [testSize]);
  useEffect(() => {
    // This timeout is necessary to get around all the page re-flow rendering
    // 50ms actually is enough on my machine but I'm adding 100ms to be safe
    const timeout = setTimeout(testSize, 150);
    return () => clearTimeout(timeout);
  }, [text]);
  const wordSpans = useMemo(
    () =>
      splitWords(text).map((value, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={`${value}-${index}`}>{value}</span>
      )),
    [text]
  );
  return (
    <Component>
      <WrapperSpan typography={typography} ref={wrapperRef}>
        <span ref={textWrapperRef}>{wordSpans}</span>
      </WrapperSpan>
    </Component>
  );
};

export default TruncatedText;
